import { CompoundSlot, SimulatedVehicle } from '@/types/CompoundMap';

export const featureId = (feature: CompoundSlot) =>
  String(feature.id || feature.properties.slotId);
export const slotLabel = (feature: CompoundSlot) =>
  feature.properties.label ||
  (feature.properties.drawingReference
    ? `${feature.properties.drawingReference.lane}-${feature.properties.drawingReference.number}`
    : feature.properties.reviewId || 'Unresolved slot');
export const isMapped = (feature: CompoundSlot) =>
  ['confirmed', 'mapped'].includes(
    feature.properties.mappingStatus.toLowerCase(),
  );

export function searchSlots(
  features: CompoundSlot[],
  input: string,
  vehicles: ReadonlyMap<string, { vin: string } | readonly { vin: string }[]>,
) {
  const term = input.trim().toUpperCase();
  if (!term) return [];
  const fields = (f: CompoundSlot) => {
    const drawing = f.properties.drawingReference;
    const value = vehicles.get(featureId(f));
    const vins = Array.isArray(value)
      ? value.map((v) => v.vin)
      : value
        ? [(value as { vin: string }).vin]
        : [];
    return [
      slotLabel(f),
      f.properties.reviewId,
      String(f.properties.number ?? ''),
      ...vins,
      ...(drawing
        ? [
            `${drawing.lane}-${drawing.number}`,
            `${drawing.lane}${drawing.number}`,
          ]
        : []),
    ];
  };
  const matches = features.filter((f) =>
    fields(f).some((v) => v.toUpperCase().includes(term)),
  );
  const exact = (f: CompoundSlot) =>
    fields(f).some((v) => v.toUpperCase() === term);
  return matches.sort((a, b) => Number(exact(b)) - Number(exact(a)));
}

/** Undo the white-paper composite while retaining the original line colour and alpha. */
export function removeCadPaper(pixels: Uint8ClampedArray) {
  for (let i = 0; i < pixels.length; i += 4) {
    const paper = Math.min(pixels[i], pixels[i + 1], pixels[i + 2]);
    const ink = 255 - paper;
    if (!ink) {
      pixels[i] = pixels[i + 1] = pixels[i + 2] = pixels[i + 3] = 0;
      continue;
    }
    pixels[i] = Math.round(((pixels[i] - paper) * 255) / ink);
    pixels[i + 1] = Math.round(((pixels[i + 1] - paper) * 255) / ink);
    pixels[i + 2] = Math.round(((pixels[i + 2] - paper) * 255) / ink);
    pixels[i + 3] = Math.round((pixels[i + 3] * ink) / 255);
  }
}

export class DemoOccupancy {
  static groups(features: CompoundSlot[]) {
    const origin = features
      .reduce(
        (sum, f) => [
          sum[0] + Number(f.properties.longitude),
          sum[1] + Number(f.properties.latitude),
        ],
        [0, 0],
      )
      .map((n) => n / features.length);
    const scaleX = 111320 * Math.cos((origin[1] * Math.PI) / 180);
    const xy = ([lng, lat]: number[]) => [
      (lng - origin[0]) * scaleX,
      (lat - origin[1]) * 111320,
    ];
    const lanes = new Map<
      string,
      Array<{ feature: CompoundSlot; order: string | number }>
    >();
    for (const feature of features) {
      const p = feature.properties;
      let key: string, order: string | number;
      if (p.block && p.lane && p.number !== null) {
        key = `lane:${p.block}:${p.lane}`;
        order = String(p.number);
      } else {
        const ring = feature.geometry.coordinates[0].slice(0, 4).map(xy);
        const edges = ring.map((a, i) => {
          const b = ring[(i + 1) % 4];
          return [b[0] - a[0], b[1] - a[1]];
        });
        edges.sort((a, b) => b[0] ** 2 + b[1] ** 2 - a[0] ** 2 - a[1] ** 2);
        let angle = Math.atan2(edges[0][1], edges[0][0]);
        if (angle < 0) angle += Math.PI;
        const direction = Math.round(angle / (Math.PI / 36)) % 36;
        angle = (direction * Math.PI) / 36;
        const [x, y] = xy([Number(p.longitude), Number(p.latitude)]);
        key = `row:${direction}:${Math.round((x * Math.cos(angle) + y * Math.sin(angle)) / 3)}`;
        order = -x * Math.sin(angle) + y * Math.cos(angle);
      }
      if (!lanes.has(key)) lanes.set(key, []);
      lanes.get(key)!.push({ feature, order });
    }
    const groups: Array<{ key: string; features: CompoundSlot[] }> = [];
    for (const [key, entries] of [...lanes].sort(([a], [b]) =>
      a.localeCompare(b),
    )) {
      entries.sort(
        (a, b) =>
          (typeof a.order === 'number'
            ? a.order - Number(b.order)
            : a.order.localeCompare(String(b.order), 'en', {
                numeric: true,
              })) || String(a.feature.id).localeCompare(String(b.feature.id)),
      );
      let segment: typeof entries = [],
        segmentIndex = 0;
      for (const entry of entries) {
        if (
          key.startsWith('row:') &&
          segment.length &&
          Number(entry.order) - Number(segment[segment.length - 1].order) > 12
        ) {
          groups.push({
            key: `${key}:${segmentIndex++}`,
            features: segment.map((x) => x.feature),
          });
          segment = [];
        }
        segment.push(entry);
      }
      if (segment.length)
        groups.push({
          key: `${key}:${segmentIndex}`,
          features: segment.map((x) => x.feature),
        });
    }
    return groups;
  }

  static vin(serial: number) {
    const draft = `DEM0TEST0T0${String(serial).padStart(6, '0')}`;
    const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
    const values: Record<string, number> = {
      A: 1,
      B: 2,
      C: 3,
      D: 4,
      E: 5,
      F: 6,
      G: 7,
      H: 8,
      J: 1,
      K: 2,
      L: 3,
      M: 4,
      N: 5,
      P: 7,
      R: 9,
      S: 2,
      T: 3,
      U: 4,
      V: 5,
      W: 6,
      X: 7,
      Y: 8,
      Z: 9,
    };
    const check =
      [...draft].reduce(
        (sum, c, i) => sum + (values[c] ?? Number(c)) * weights[i],
        0,
      ) % 11;
    return (
      draft.slice(0, 8) + (check === 10 ? 'X' : String(check)) + draft.slice(9)
    );
  }

  static assign(features: CompoundSlot[], ratio = 0.8, seed = 0x5eed0080) {
    let value = seed >>> 0;
    const random = () => {
      value ^= value << 13;
      value ^= value >>> 17;
      value ^= value << 5;
      return (value >>> 0) / 4294967296;
    };
    const groups = DemoOccupancy.groups(features).map((group) => ({
      ...group,
      count: Math.floor(group.features.length * ratio),
      remainder: (group.features.length * ratio) % 1,
    }));
    const remaining =
      Math.round(features.length * ratio) -
      groups.reduce((sum, group) => sum + group.count, 0);
    [...groups]
      .sort((a, b) => b.remainder - a.remainder || a.key.localeCompare(b.key))
      .slice(0, remaining)
      .forEach((group) => {
        group.count += 1;
      });
    const slots = groups.flatMap((group) =>
      group.features.slice(0, group.count),
    );
    const models = [
      ['BMW', '320i M Sport'],
      ['BMW', 'X1 sDrive20i'],
      ['BMW', 'X3 xDrive20d'],
      ['BMW', 'i4 eDrive40'],
      ['MINI', 'Cooper S'],
      ['MINI', 'Countryman SE'],
      ['BMW', '520i M Sport'],
    ];
    const colours = [
      ['Alpine White', '#f3f5f7'],
      ['Black Sapphire', '#344253'],
      ['Mineral Grey', '#87949f'],
      ['Portimao Blue', '#356da4'],
      ['Melting Silver', '#c3cbd1'],
      ['Chili Red', '#a44e54'],
      ['British Racing Green', '#427262'],
    ];
    const vehicles = new Map<string, SimulatedVehicle>();
    for (let i = 0; i < slots.length; i += 1) {
      const [make, model] = models[Math.floor(random() * models.length)];
      const [colour, colourHex] =
        colours[Math.floor(random() * colours.length)];
      const number = String(i + 1).padStart(5, '0');
      vehicles.set(String(slots[i].id || slots[i].properties.slotId), {
        id: `SIM-${number}`,
        vin: DemoOccupancy.vin(i + 1),
        registration: `DEMO ${number}`,
        make,
        model,
        colour,
        colourHex,
        simulated: true,
      });
    }
    return vehicles;
  }
}

export class SlotSpatialIndex {
  cells = new Map<
    string,
    Array<{
      feature: CompoundSlot;
      box: { west: number; east: number; south: number; north: number };
    }>
  >();
  cellWidth = 0.0005;
  cellHeight = 0.0003;
  constructor(features: CompoundSlot[]) {
    this.cellWidth = 0.0005;
    this.cellHeight = 0.0003;
    for (const feature of features) {
      const ring = feature.geometry.coordinates[0];
      const box = {
        west: Infinity,
        east: -Infinity,
        south: Infinity,
        north: -Infinity,
      };
      for (const [lng, lat] of ring) {
        box.west = Math.min(box.west, lng);
        box.east = Math.max(box.east, lng);
        box.south = Math.min(box.south, lat);
        box.north = Math.max(box.north, lat);
      }
      const entry = { feature, box };
      for (
        let x = Math.floor(box.west / this.cellWidth);
        x <= Math.floor(box.east / this.cellWidth);
        x += 1
      ) {
        for (
          let y = Math.floor(box.south / this.cellHeight);
          y <= Math.floor(box.north / this.cellHeight);
          y += 1
        ) {
          const key = `${x},${y}`;
          if (!this.cells.has(key)) this.cells.set(key, []);
          this.cells.get(key)!.push(entry);
        }
      }
    }
  }

  static inRing(lng: number, lat: number, ring: number[][]) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (
        yi > lat !== yj > lat &&
        lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
      )
        inside = !inside;
    }
    return inside;
  }

  at(lng: number, lat: number) {
    const candidates =
      this.cells.get(
        `${Math.floor(lng / this.cellWidth)},${Math.floor(lat / this.cellHeight)}`,
      ) || [];
    return candidates
      .filter(({ feature, box }) => {
        if (
          lng < box.west ||
          lng > box.east ||
          lat < box.south ||
          lat > box.north
        )
          return false;
        const rings = feature.geometry.coordinates;
        return (
          SlotSpatialIndex.inRing(lng, lat, rings[0]) &&
          !rings
            .slice(1)
            .some((ring) => SlotSpatialIndex.inRing(lng, lat, ring))
        );
      })
      .map((entry) => entry.feature);
  }
}
